/**
 * Reads the current Word document and extracts every merge-tag token — from
 * both plain-text authoring (what the add-in inserts, e.g. `{{CF_NAME}}`,
 * `{{TableStart:Options}}`, `{{Code}}` as literal text) and real MERGEFIELDs
 * (what legacy templates use, stored as `<w:fldChar>` structures the Aspose
 * engine parses at merge time).
 *
 * Classification mirrors what the backend mapper exposes:
 *   - Scalar tags: `CF_*` / `REG_[N]_*` / `CO_*` names
 *   - Region markers: `TableStart:X` and `TableEnd:X`
 *   - Column tags: any other bare name (assumed to live inside a region)
 *
 * Names are deduplicated across sources. Whitespace + Word field formatting
 * switches (`\* MERGEFORMAT`, `\# $#,#`) inside the token are ignored — we
 * only need the identifier. Chevron-wrapped tokens (`«X»`) are still scanned
 * for backwards compatibility with legacy templates and with Word's display
 * form of unmerged MERGEFIELDs, but the add-in itself no longer emits them
 * (they were not recognised by Aspose's region-merge path — #2391).
 */

export interface DocumentTokens {
  scalarTags: string[];
  regionNames: string[];
  columnTags: string[];
}

// `{{X}}` mustache-wrapped token — matches everything the add-in emits:
//   * Scalar tags:     {{CF_NAME}}, {{REG_1_FIRST_NAME}}, {{CO_LOGO}}
//   * Region markers:  {{TableStart:Options}}, {{TableEnd:Options}}
//   * Column tags:     {{Code}}, {{Name}}, {{Quantity}}
// Allows Word field switches after the tag name (`{{ CF_X \* Caps }}`).
// `classifyNames` sorts the captured identifiers into the three buckets.
const CURLY_TAG_RE = /\{\{\s*([^}\n\r]+?)\s*\}\}/g;

// `«X»` chevron-wrapped tokens — captured in body text when Word's own display
// form of an unmerged MERGEFIELD renders as chevrons, and in legacy templates
// authored before we switched to mustache. The add-in itself no longer emits
// chevrons (Aspose's region-merge path did not recognise them — #2391).
const CHEVRON_RE = /«([^»\n\r]+)»/g;

// `MERGEFIELD X` field code — the code the Word field carries in the underlying
// docx XML. Also `MERGEFIELD image(50;50):CO_LOGO` — we grab the whole token
// after the first space and strip the `image(...):` prefix if present.
const MERGEFIELD_RE = /^\s*MERGEFIELD\s+(?:image\([^)]*\):)?(\S+)/i;

/** Reads the doc, extracts + classifies every tag token, deduped. */
export async function scanDocumentTags(): Promise<DocumentTokens> {
  return Word.run(async (ctx) => {
    const body = ctx.document.body;
    const bodyRange = body.getRange();
    body.fields.load('items/code');
    bodyRange.load('text');
    await ctx.sync();

    const rawNames = new Set<string>();

    // 1. Plain-text mustache tokens — the primary format the add-in inserts
    //    (scalars, region markers, column tags — all `{{X}}`).
    for (const match of bodyRange.text.matchAll(CURLY_TAG_RE)) {
      rawNames.add(match[1]);
    }

    // 2. Chevron-wrapped tokens in body text — Word's display form for an
    //    unmerged MERGEFIELD, and legacy templates authored before the
    //    mustache switch. Extract for backwards compatibility.
    for (const match of bodyRange.text.matchAll(CHEVRON_RE)) {
      rawNames.add(match[1].trim());
    }

    // 3. Real MERGEFIELDs (legacy templates, Word's Insert → Field flow, etc.).
    //    Field.code returns the raw field instruction like "MERGEFIELD CF_X".
    for (const field of body.fields.items) {
      const code = field.code ?? '';
      const match = code.match(MERGEFIELD_RE);
      if (match !== null) {
        rawNames.add(match[1]);
      }
    }

    return classifyNames(rawNames);
  });
}

/** Same classifier the scanner uses — exposed for testing / dev-tooling. */
export function classifyNames(rawNames: Iterable<string>): DocumentTokens {
  const scalarTags = new Set<string>();
  const regionNames = new Set<string>();
  const columnTags = new Set<string>();

  for (const nameWithSwitches of rawNames) {
    // Strip any Word field switches after the identifier ("CF_X \* Caps").
    const name = nameWithSwitches.split(/\s|\\/)[0]?.trim();
    if (!name) continue;

    if (name.startsWith('TableStart:')) {
      regionNames.add(name.slice('TableStart:'.length));
    } else if (name.startsWith('TableEnd:')) {
      regionNames.add(name.slice('TableEnd:'.length));
    } else if (/^(?:CF|REG|CO)_[A-Z0-9_]+$/.test(name)) {
      // CF_* (config), REG_[N]_* (registrant slots), CO_* / CO_SIGNER_* (company block).
      // See CURLY_SCALAR_RE at the top of the file for the same prefix family.
      scalarTags.add(name);
    } else if (/^[A-Za-z][A-Za-z0-9_]*$/.test(name)) {
      // Bare identifier — assumed to be a region column tag. Anything else
      // (punctuation, spaces, numbers-only) is ignored as noise.
      columnTags.add(name);
    }
  }

  return {
    scalarTags: [...scalarTags].sort(),
    regionNames: [...regionNames].sort(),
    columnTags: [...columnTags].sort(),
  };
}
